import { FormControl, FormGroup } from '@angular/forms';
import {
  NOTIFICATION_TYPE_TO_API,
  NotificationPreferences,
  NotificationPreferenceType,
  SaveNotificationPreferenceChange,
  SaveNotificationPreferencesRequest
} from '../models/notification-preferences.model';

export const DEFAULT_YES_NOTIFICATION_TYPES = ['billing', 'enrollment', 'cancellation'] as const;

export type NotificationPreferencesForm = FormGroup<{
  billing: FormControl<boolean>;
  enrollment: FormControl<boolean>;
  cancellation: FormControl<boolean>;
}>;

export function createNotificationPreferencesForm(): NotificationPreferencesForm {
  return new FormGroup({
    billing: new FormControl(false, { nonNullable: true }),
    enrollment: new FormControl(false, { nonNullable: true }),
    cancellation: new FormControl(false, { nonNullable: true })
  });
}

function syncTypeControl(control: FormControl<boolean>, enabled: boolean | null): void {
  if (enabled === null) {
    control.reset(false, { emitEvent: false });
    control.disable({ emitEvent: false });
    return;
  }

  control.enable({ emitEvent: false });
  control.setValue(enabled, { emitEvent: false });
}

export function syncNotificationPreferencesForm(
  form: NotificationPreferencesForm,
  preferences: NotificationPreferences
): void {
  syncTypeControl(form.controls.billing, preferences.billingEnabled);
  syncTypeControl(form.controls.enrollment, preferences.enrollmentEnabled);
  syncTypeControl(form.controls.cancellation, preferences.cancellationEnabled);
}


/**
 * First-login defaults: switch On every type this user is allowed to set.
 * Defaults only: the user can switch any of them Off before saving.
 */
export function applyFirstLoginNotificationDefaults(form: NotificationPreferencesForm): void {
  for (const type of DEFAULT_YES_NOTIFICATION_TYPES) {
    if (form.controls[type].disabled) continue;
    form.controls[type].setValue(true, { emitEvent: false });
  }
}

export function shouldShowNotificationType(enabled: boolean | null): boolean {
  return enabled !== null;
}

export function buildSaveNotificationPreferencesRequest(
  form: NotificationPreferencesForm,
  preferences: NotificationPreferences
): SaveNotificationPreferencesRequest {
  if (!preferences.mCode) {
    return { changes: [] };
  }

  const changes: SaveNotificationPreferenceChange[] = [];

  for (const type of ['billing', 'enrollment', 'cancellation'] as const) {
    const enabledState = notificationTypeEnabled(preferences, type);
    if (enabledState === null) {
      continue;
    }

    changes.push({
      notificationType: NOTIFICATION_TYPE_TO_API[type],
      mCode: preferences.mCode,
      enabled: form.controls[type].getRawValue()
    });
  }

  return { changes };
}

export function notificationTypeEnabled(
  preferences: NotificationPreferences,
  type: NotificationPreferenceType
): boolean | null {
  switch (type) {
    case 'billing':
      return preferences.billingEnabled;
    case 'enrollment':
      return preferences.enrollmentEnabled;
    case 'cancellation':
      return preferences.cancellationEnabled;
  }
}

export function notificationPreferencesFormIsDirty(
  form: NotificationPreferencesForm,
  preferences: NotificationPreferences
): boolean {
  for (const type of ['billing', 'enrollment', 'cancellation'] as const) {
    const enabledState = notificationTypeEnabled(preferences, type);
    if (enabledState === null) {
      continue;
    }
    if (form.controls[type].getRawValue() !== enabledState) {
      return true;
    }
  }
  return false;
}
